import { useMemo, useState } from "react";
import { ScrollText } from "lucide-react";
import { usePageInsightsPanel } from "@/app/pageActions/PageActionsContext";
import { isApiClientError } from "@/shared/api/errors";
import {
  ListPageTemplate,
  SearchStatusFilterBar,
} from "@/shared/ui/patterns/ListPageTemplate";
import {
  Badge,
  Banner,
  ContentModal,
  DataTable,
  DetailField,
  DetailFieldGrid,
  ModuleInsights,
  TableIdLink,
} from "@/shared/ui";
import {
  formatDateDisplay,
  parseInputDate,
} from "@/shared/ui/core/datePickerUtils";
import { sysAuditInsights } from "@/modules/sys/model/sysListInsights";
import { useAuditListQuery } from "@/modules/sys/queries/useSysQueries";
import {
  auditActionLabel,
  auditEntityTypeFilterOptions,
  auditEntityTypeLabel,
} from "@/modules/sys/model/auditLabels";
import {
  auditHasDelta,
  formatAuditJson,
  type AuditLog,
} from "@/modules/sys/model/types";

function formatCreatedAt(iso: string) {
  const parsed = parseInputDate(iso);
  if (!parsed) return "—";
  return formatDateDisplay(parsed.date, true);
}

const ENTITY_TYPE_OPTIONS = auditEntityTypeFilterOptions();

export function AuditListPage() {
  const list = useAuditListQuery();
  const [selectedAudit, setSelectedAudit] = useState<AuditLog | null>(null);

  const insights = useMemo(
    () =>
      sysAuditInsights({
        total: list.total,
        onClear: () => list.clearFilters(),
      }),
    [list],
  );

  const insightsOpen = usePageInsightsPanel({
    initialOpen: true,
    alertCount: 0,
    enabled: !list.isError,
  });

  return (
    <ListPageTemplate
      loading={list.isLoading}
      errorTitle={list.isError ? "Không tải được audit" : undefined}
      errorDescription={
        list.isError && isApiClientError(list.error) ? list.error.message : undefined
      }
      onRetry={() => void list.refetch()}
      insights={
        insightsOpen ? (
          <ModuleInsights {...insights} label="Tóm tắt audit" showDistribution={false} />
        ) : null
      }
      filtersOpen={list.filtersOpen}
      filterActiveCount={list.activeFilterCount}
      filter={
        <SearchStatusFilterBar
          open={list.filtersOpen}
          q={list.q}
          onQChange={list.setQ}
          searchPlaceholder="Tìm kiếm thao tác / thực thể / mã"
          extraSelect={{
            id: "entityType",
            label: "Loại thực thể",
            value: list.filters.entityType ?? "all",
            options: ENTITY_TYPE_OPTIONS,
            onValueChange: (v) => list.setFilter("entityType", v),
          }}
          onClearAll={list.clearFilters}
        />
      }
      pagination={{
        page: list.page,
        pageSize: list.pageSize,
        total: list.total,
        loading: list.isFetching,
        onPageChange: list.setPage,
        onPageSizeChange: list.setPageSize,
      }}
    >
      <div className="flex flex-col gap-3">
          <DataTable<AuditLog>
            getRowId={(a) => a.id}
            rows={list.items}
            pageLoading={list.isFetching && !list.isLoading}
            loadingText="Đang tải trang..."
            emptyKeepHeader
            emptyTitle="Chưa có sự kiện audit"
            emptyDescription="Đổi từ khóa hoặc bộ lọc."
            emptyIcon={<ScrollText className="size-6" aria-hidden />}
            rowNumberStart={list.rowNumberStart}
            onRowClick={(a) => setSelectedAudit(a)}
            loadMore={
              list.isDesktop
                ? undefined
                : {
                    hasMore: list.hasMore,
                    onLoadMore: list.loadMore,
                    shown: list.shown,
                    total: list.total,
                    loading: list.isFetching,
                  }
            }
            columns={[
              {
                id: "at",
                header: "Thời gian",
                align: "center",
                width: "1%",
                cell: (a) => formatCreatedAt(a.createdAt),
              },
              {
                id: "action",
                header: "Thao tác",
                cell: (a) => auditActionLabel(a.action),
              },
              {
                id: "type",
                header: "Thực thể",
                cell: (a) => auditEntityTypeLabel(a.entityType),
              },
              {
                id: "eid",
                header: "ID thực thể",
                cell: (a) => <TableIdLink>{a.entityId}</TableIdLink>,
              },
              {
                id: "delta",
                header: "Delta",
                cell: (a) =>
                  auditHasDelta(a) ? (
                    <Badge tone="info" label="Có" />
                  ) : (
                    <span className="text-text-muted">—</span>
                  ),
              },
              {
                id: "actor",
                header: "Người thực hiện",
                cell: (a) =>
                  a.actorUsername ??
                  (a.actorUserId ? a.actorUserId.slice(0, 8) : "—"),
                mono: true,
              },
            ]}
          />
      </div>

      <ContentModal
        open={selectedAudit != null}
        onOpenChange={(open) => {
          if (!open) setSelectedAudit(null);
        }}
        title="Chi tiết sự kiện kiểm toán"
        description={
          selectedAudit
            ? `${auditEntityTypeLabel(selectedAudit.entityType)} · ${auditActionLabel(selectedAudit.action)}`
            : undefined
        }
        icon={<ScrollText className="size-4" />}
        iconTone="info"
        iconVariant="badge"
        headerTone="info"
        size="xl"
        lockOutsideClose={false}
      >
        {selectedAudit ? (
          <div className="space-y-4">
            <DetailFieldGrid columns={2}>
              <DetailField label="Thời gian">
                {formatCreatedAt(selectedAudit.createdAt)}
              </DetailField>
              <DetailField label="Người thực hiện" mono>
                {selectedAudit.actorUsername ??
                  (selectedAudit.actorUserId
                    ? selectedAudit.actorUserId.slice(0, 8)
                    : "—")}
              </DetailField>
              <DetailField label="Thực thể">
                {auditEntityTypeLabel(selectedAudit.entityType)}
              </DetailField>
              <DetailField label="Thao tác">
                {auditActionLabel(selectedAudit.action)}
              </DetailField>
              <DetailField label="ID thực thể" mono fullWidth>
                {selectedAudit.entityId}
              </DetailField>
              {selectedAudit.ip ? (
                <DetailField label="Địa chỉ IP" mono>
                  {selectedAudit.ip}
                </DetailField>
              ) : null}
            </DetailFieldGrid>

            <div className="border-t border-border pt-3">
              <h4 className="m-0 mb-2 text-label font-semibold text-text">
                Thay đổi dữ liệu (Delta)
              </h4>
              {auditHasDelta(selectedAudit) ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="m-0 mb-1.5 text-caption font-semibold text-text-secondary">
                      Trước thay đổi
                    </p>
                    <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-control border border-border bg-bg/50 p-3 font-mono text-caption text-text">
                      {formatAuditJson(selectedAudit.beforeJson)}
                    </pre>
                  </div>
                  <div>
                    <p className="m-0 mb-1.5 text-caption font-semibold text-text-secondary">
                      Sau thay đổi
                    </p>
                    <pre className="m-0 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-control border border-border bg-bg/50 p-3 font-mono text-caption text-text">
                      {formatAuditJson(selectedAudit.afterJson)}
                    </pre>
                  </div>
                </div>
              ) : (
                <Banner
                  tone="info"
                  title="Không có before/after"
                  description="Sự kiện này không ghi nhận thay đổi dữ liệu (delta)."
                />
              )}
            </div>
          </div>
        ) : null}
      </ContentModal>
    </ListPageTemplate>
  );
}
